import { RIGHTS } from "@/enums";
import { useUserContext } from "@/providers";

interface IAllRights {
  adminRights: {
    canViewApiDocumentation: boolean;
    canViewProjectMilestones: boolean;
    canEditProjectRoles: boolean;
    canViewNotificationGroups: boolean;
    canViewPermissions: boolean;
    canViewTemplates: boolean;
    canViewForms: boolean;
    canViewUserReplacement: boolean;
    canViewBaselineDates: boolean;
    canViewUsers: boolean;
    canViewProjectRoles: boolean;
    canViewApprovalWorkFlows: boolean;
    canEditPermissions: boolean;
    canEditAllProjects: boolean;
  };
  canAccessViper: boolean;
  canCreateIntake: boolean;
  canViewCreateManageInitiatives: boolean;
  canEditPermissions: boolean;
  canEditProjectRoles: boolean;
  canViewApiDocumentation: boolean;
  canViewApprovalWorkFlows: boolean;
  canViewBaselineDates: boolean;
  canViewForms: boolean;
  canViewNotificationGroups: boolean;
  canViewPermissions: boolean;
  canViewProjectMilestones: boolean;
  canViewProjectRoles: boolean;
  canViewTemplates: boolean;
  canViewUserReplacement: boolean;
  canViewUsers: boolean;
  hasSomeAdminRights: boolean;
}

const useAllRights = (): IAllRights => {
  const { hasRight } = useUserContext();

  const checkForSomeAdminRights = (rights: {
    [key: string]: boolean;
  }): boolean => Object.keys(rights).some((key) => rights[key]);

  const adminRights = {
    canViewUsers: hasRight([RIGHTS.readUsers]),
    canEditAllProjects: hasRight([RIGHTS.writeAllProjects]),
    canViewTemplates: hasRight([
      RIGHTS.readTemplates,
      RIGHTS.writeTemplates,
      RIGHTS.publishTemplates,
    ]),
    canViewApiDocumentation: hasRight([RIGHTS.readApiDocumentation]),
    canViewPermissions: hasRight([
      RIGHTS.writePermissions,
      RIGHTS.readPermissions,
    ]),
    canEditPermissions: hasRight([RIGHTS.writePermissions]),
    // Should eventually use its own Role Rights
    canViewProjectRoles: hasRight([
      RIGHTS.readTemplates,
      RIGHTS.writeTemplates,
      RIGHTS.publishTemplates,
    ]),
    canEditProjectRoles: hasRight([RIGHTS.writeTemplates]),
    // Should eventually use Project Milestone Rights
    canViewProjectMilestones: hasRight([
      RIGHTS.readTemplates,
      RIGHTS.writeTemplates,
      RIGHTS.publishTemplates,
    ]),
    canEditCompletedProject: hasRight([RIGHTS.editCompletedProject]),
    canViewForms: hasRight([RIGHTS.editForm]),
    canViewApprovalWorkFlows: hasRight([
      RIGHTS.readApprovalWorkFlows,
      RIGHTS.writeApprovalWorkFlows,
    ]),
    canViewNotificationGroups: hasRight([
      RIGHTS.readNotificationGroups,
      RIGHTS.writeNotificationGroups,
    ]),
    canViewBaselineDates: hasRight([
      RIGHTS.readBaselineDates,
      RIGHTS.writeBaselineDates,
    ]),
    canViewUserReplacement: hasRight([
      RIGHTS.massReplaceUsers,
      RIGHTS.replaceProjectUsers,
    ]),
  };

  const basicUserRights = {
    canAccessViper: hasRight([RIGHTS.viperUser]),
    canCreateIntake: hasRight([RIGHTS.createIntake]),
    canViewCreateManageInitiatives: hasRight([
      RIGHTS.viewCreateManageInitiatives,
    ]),
  };

  const hasSomeAdminRights = checkForSomeAdminRights(adminRights);

  return {
    hasSomeAdminRights,
    adminRights,
    ...adminRights,
    ...basicUserRights,
  };
};

export default useAllRights;
